import React, { useState } from 'react';
import { X, Type, Info } from 'lucide-react';
import Button from './ui/Button';
import DatePicker from './ui/DatePicker';
import { API_PATH, request } from '../config/api';
import styles from './CreateEventModal.module.css';

const API_URL = `${API_PATH}/events`;

const CreateEventModal = ({ isOpen, onClose, onCreateEvent }) => {
    const [eventName, setEventName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const today = new Date().toISOString().split('T')[0];
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const resetForm = () => {
        setEventName('');
        setStartDate('');
        setEndDate('');
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Client-side date validation — the native <input type="date">'s built-in `required`
        // used to block submission here; DatePicker has no such browser-native mechanism, so
        // this check does the same job explicitly.
        if (!startDate || !endDate) {
            setError('Please select both a start and end date.');
            return;
        }
        if (new Date(endDate) < new Date(startDate)) {
            setError('End date cannot be before start date.');
            return;
        }
        const durationDays = (new Date(endDate) - new Date(startDate)) / (24 * 60 * 60 * 1000);
        if (durationDays > 365) {
            setError('Event duration cannot exceed 365 days.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await request(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: eventName, startDate, endDate }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to create event');
            }

            onCreateEvent(data); // pass full event object (with DB id, code, etc.)
            resetForm();
            onClose();
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        resetForm();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.modalHeader}>
                    <div>
                        <h2 className={styles.modalTitle}>Create Your Event</h2>
                        <p className={styles.modalSubtitle}>
                            Set up your real-time audience engagement platform in seconds.
                        </p>
                    </div>
                    <button
                        className={styles.closeBtn}
                        onClick={handleCancel}
                        disabled={isLoading}
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Error Banner */}
                {error && (
                    <div className={styles.errorBanner}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 12.5a5.5 5.5 0 110-11 5.5 5.5 0 010 11zm-.75-8.25a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zm.75 6a.875.875 0 110-1.75.875.875 0 010 1.75z" />
                        </svg>
                        {error}
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className={styles.form}>
                    {/* Event Name */}
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Event Name</label>
                        <div className={styles.inputWrapper}>
                            <Type size={18} className={styles.inputIcon} aria-hidden />
                            <input
                                type="text"
                                className={styles.input}
                                placeholder="e.g. Annual Tech Summit 2026"
                                value={eventName}
                                onChange={(e) => setEventName(e.target.value)}
                                required
                                disabled={isLoading}
                            />
                        </div>
                        <p className={styles.fieldHint}>Choose a short, clear name so participants can recognize it quickly.</p>
                    </div>

                    {/* Date Fields */}
                    <div className={styles.dateRow}>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Start Date</label>
                            <DatePicker
                                value={startDate}
                                min={today}
                                onChange={(iso) => {
                                    setStartDate(iso);
                                    // Clear end date if it's now before new start date
                                    if (endDate && endDate < iso) setEndDate('');
                                }}
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>End Date</label>
                            <DatePicker
                                value={endDate}
                                min={startDate}
                                max={startDate ? (() => { const d = new Date(startDate); d.setDate(d.getDate() + 365); return d.toISOString().slice(0, 10); })() : undefined}
                                onChange={(iso) => setEndDate(iso)}
                                disabled={isLoading}
                            />
                        </div>
                    </div> 
                    <p className={styles.fieldHint} style={{ marginTop: '1rem', gridColumn: '1 / -1' }}>Event can be up to 365 days. Each poll costs coins;  extending the event later costs coins per 4-day block.</p>

                    {/* Info Message */}
                    <div className={styles.infoBox}>
                        <Info size={16} className={styles.infoIcon} aria-hidden />
                        <p className={styles.infoText}>
                            Your event code will be generated automatically once the event is created.
                            You can customize it in the settings later.
                        </p>
                    </div>

                    {/* Buttons */}
                    <div className={styles.buttonGroup}>
                        <Button type="button" variant="secondary" size="sm" className={styles.cancelBtn} onClick={handleCancel} disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button type="submit" size="sm" className={styles.createBtn} disabled={isLoading} loading={isLoading}>
                            {isLoading ? 'Creating...' : 'Create Event'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateEventModal;
